import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import HomePage from './pages/HomePage';
import RafflesPage from './pages/RafflesPage';
import RaffleDetailPage from './pages/RaffleDetailPage';
import SorteosPage from './pages/SorteosPage';
import SorteoDetailPage from './pages/SorteoDetailPage';
import AdminDashboard from './pages/AdminDashboard';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AdminApprovalPage from './pages/AdminApprovalPage';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configurar axios globalmente
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auto-logout on 401 (token expired) or 403 SUBSCRIPTION_EXPIRED
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const isAuthEndpoint = error?.config?.url?.includes('/auth/login') || error?.config?.url?.includes('/auth/register');
    const shouldLogout =
      (status === 401 && !isAuthEndpoint && localStorage.getItem('admin_token')) ||
      (status === 403 && detail === 'SUBSCRIPTION_EXPIRED' && !isAuthEndpoint && localStorage.getItem('admin_token'));
    if (shouldLogout) {
      localStorage.removeItem('admin_token');
      if (window.location.pathname.startsWith('/admin')) {
        // Send them to login with a flag so we can show the renewal screen
        window.location.href = '/login?expired=1';
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // Keep-alive: ping al backend cada 4 minutos para evitar que el servidor se duerma
  useEffect(() => {
    const keepAlive = () => {
      fetch(`${API}/`, { method: 'GET' }).catch(() => {});
    };
    keepAlive();
    const interval = setInterval(keepAlive, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      try {
        await axios.get(`${API}/auth/me`);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem('admin_token');
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    // Replace history so "back" doesn't bring the user back to /admin
    window.history.replaceState(null, '', '/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="App">
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/rifas" element={<RafflesPage />} />
          <Route path="/rifas/:adminSlug" element={<RafflesPage />} />
          <Route path="/rifa/:raffleId" element={<RaffleDetailPage />} />
          <Route path="/sorteos/:adminSlug" element={<SorteosPage />} />
          <Route path="/sorteo/:sorteoId" element={<SorteoDetailPage />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/admin-approval" element={<AdminApprovalPage />} />
          <Route
            path="/admin/*"
            element={
              isAuthenticated ? (
                <AdminDashboard onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
